import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import Grid from "@material-ui/core/Grid";
import { Button } from "@material-ui/core";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import HelpIcon from "@material-ui/icons/Help";
import LinearProgress from "@material-ui/core/LinearProgress";

import CustomInput from "@Components/CustomInput/CustomInput";
import HoverTooltip from "@Components/Tooltip/HoverTooltip";
import CustomSelect from "@Components/CustomSelect";
import eightAxios from "configs/axiosConfig";
import {
  COMMENT_URL,
  GRIEVANCE_LANGUAGE_CHOICES,
  GRIEVANCE_TRANSLATION,
  GRIEVANCE_TRANSLATION_RESULT,
} from "configs/constants";
import { useInterval } from "utils";

const BO_LANGUAGE_SELECTION = [
  { id: "ms-MY", name: "BAHASA MALAYSIA" },
  { id: "en-US", name: "ENGLISH" },
];

const CommentTranslationContainer = (props) => {
  const {
    comment,
    comments,
    setComments,
    setShowTranslatedText,
    renderOnly,
  } = props;
  const { t } = useTranslation();
  const [languageOptions, setLanguageOptions] = useState([]);
  const [translateFrom, setTranslateFrom] = useState("");
  const [loading, setLoading] = useState(false);
  const [translationCount, setTranslationCount] = useState(0);
  const [translatedText, setTranslatedText] = useState(comment.translated_text);
  const [translateTo, setTranslateTo] = useState("");

  useEffect(() => {
    if (comment.allowed_translation_count) {
      setTranslationCount(comment.allowed_translation_count);
    }
  }, [comment.allowed_translation_count]);

  useEffect(() => {
    eightAxios
      .get(GRIEVANCE_LANGUAGE_CHOICES)
      .then((response) => {
        response.data.forEach(function (option) {
          option.name = option.name.toUpperCase();
        });
        setLanguageOptions(response.data);
      })
      .catch((error) => {
        console.error(error);
      });
  }, []);

  const handleSubmitTranslation = () => {
    setLoading(true);
    const payload = {
      comment_pk: comment.id,
      from_language: translateFrom,
      to_language: translateTo,
    };
    eightAxios
      .post(GRIEVANCE_TRANSLATION, payload)
      .then((response) => {
        setTranslatedText(response.data.status);

        const decreaseTranslationCountPayload = {
          allowed_translation_count: translationCount - 1,
        };
        eightAxios
          .patch(
            `${COMMENT_URL}${comment.id}/`,
            decreaseTranslationCountPayload
          )
          .then((response) => {
            setTranslationCount(response.data.allowed_translation_count);
          })
          .catch((error) => {
            console.error(error);
          });
      })
      .catch((error) => {
        console.error(error);
        setTranslatedText("Error translating. Please try again.");
        setLoading(false);
      });
  };

  const handleTranslationConfirmation = (event) => {
    event.preventDefault();
    const payload = {
      translated_text: translatedText,
      is_translated: true,
      translated_from: translateFrom,
    };
    eightAxios
      .patch(`${COMMENT_URL}${comment.id}/`, payload)
      .then((response) => {
        if (response.status === 200) {
          const data = response.data;
          setComments(
            comments.map((record) =>
              record.id === data.id ? { ...record, ...data } : record
            )
          );
        }
      })
      .catch((error) => {
        console.error(error);
      });
    setShowTranslatedText(true);
  };

  useInterval(
    async () => {
      const resp = await eightAxios.get(GRIEVANCE_TRANSLATION_RESULT);
      if (resp.status === 200) {
        setTranslatedText(resp.data.result);
        setLoading(false);
      }
    },
    loading ? 2000 : null
  );

  return renderOnly ? (
    <Grid style={{ padding: "5px" }} container spacing={2}>
      <Grid item container xs={12}>
        <Grid item xs={12}>
          <CustomInput
            multiline
            rows={4}
            control="input"
            label="Translated Text"
            defaultValue={translatedText}
            value={translatedText}
            name="translated_text"
            disabled
          />
        </Grid>
        {loading ? (
          <Grid item xs={12}>
            <LinearProgress />
          </Grid>
        ) : null}
        <Grid item container spacing={2} xs={12} justify="flex-end">
          {comment.is_translated ? (
            <Grid item>
              <Button
                variant="contained"
                style={{
                  borderRadius: 0,
                  top: "2px",
                  textTransform: "capitalize",
                }}
                onClick={() => setShowTranslatedText(true)}
              >
                Cancel
              </Button>
            </Grid>
          ) : null}
        </Grid>
      </Grid>
    </Grid>
  ) : (
    <Grid style={{ padding: "5px" }} container spacing={2}>
      <Grid item container spacing={1} xs={12} alignItems="center">
        <Grid item xs={12} sm={6} md={6} lg={4}>
          <CustomSelect
            label={t("Translate From")}
            required
            options={
              comment.is_additional_information
                ? languageOptions
                : BO_LANGUAGE_SELECTION
            }
            value={translateFrom}
            onChange={(event) => setTranslateFrom(event.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={6} lg={4}>
          <CustomSelect
            label="Translate To"
            required
            options={
              comment.is_additional_information
                ? BO_LANGUAGE_SELECTION
                : languageOptions
            }
            value={translateTo}
            onChange={(event) => setTranslateTo(event.target.value)}
          />
        </Grid>
        <Grid item container spacing={2} xs={12} lg={4}>
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmitTranslation}
              disabled={
                loading ||
                !translateTo ||
                !translateFrom ||
                translationCount <= 0
              }
              style={{
                borderRadius: 0,
                top: "2px",
                textTransform: "capitalize",
              }}
            >
              Translate
            </Button>
          </Grid>
          <Grid item style={{ position: "relative" }}>
            <Typography>
            {t("Allow change")}  {translationCount} {t("times")}{" "}
              <HoverTooltip
                title={ `${t("You have")} ${translationCount} ${t("translation left.")}`}
              >
                <IconButton>
                  <HelpIcon style={{ position: "absolute" }} />
                </IconButton>
              </HoverTooltip>
            </Typography>
          </Grid>
        </Grid>
      </Grid>
      <Grid item container xs={12}>
        <Grid item xs={12}>
          <CustomInput
            multiline
            rows={4}
            control="input"
            label="Translated Text"
            defaultValue={translatedText}
            value={translatedText}
            name="translated_text"
            disabled
          />
        </Grid>
        {loading ? (
          <Grid item xs={12}>
            <LinearProgress />
          </Grid>
        ) : null}
        <Grid item container spacing={2} xs={12} justify="flex-end">
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              style={{
                borderRadius: 0,
                top: "2px",
                textTransform: "capitalize",
              }}
              disabled={loading || !translatedText}
              onClick={handleTranslationConfirmation}
            >
              {t("Confirm")}
            </Button>
          </Grid>
          {comment.is_translated ? (
            <Grid item>
              <Button
                variant="contained"
                style={{
                  borderRadius: 0,
                  top: "2px",
                  textTransform: "capitalize",
                }}
                onClick={() => setShowTranslatedText(true)}
              >
                Cancel
              </Button>
            </Grid>
          ) : null}
        </Grid>
      </Grid>
    </Grid>
  );
};
CommentTranslationContainer.propTypes = {
  comment: PropTypes.object,
  comments: PropTypes.array,
  setComments: PropTypes.func,
  setShowTranslatedText: PropTypes.func,
  userLanguage: PropTypes.string,
  renderOnly: PropTypes.bool,
};

export default CommentTranslationContainer;
